import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import type { PathParams } from '#src/lib/routing/generateSportPath.ts';
import {
  SPORT_PATH_MAP,
  generateSportPath,
  matchAnySportPath,
} from '#src/lib/routing/generateSportPath.ts';
import { sportFromPath } from '#src/lib/routing/sportFromPath.ts';

import { SportContext, sportAnalyticTag } from './index.ts';

type Props = {
  children: ReactNode;
  sport?: Sport;
};

export const SportProvider = ({ children, sport: defaultSport }: Props) => {
  const { pathname } = useLocation();
  const [sport, setSport] = useState<Sport>(
    defaultSport || sportFromPath(pathname)
  );

  const value = useMemo(
    () => ({
      sport,
      sportAnalyticsTag: sportAnalyticTag[sport],
      setSport,
      // wrap the `generateSportPath` to handle the default sport
      generateSportContextPath: <Path extends string>(
        path: Path,
        {
          params,
          sport: s = sport,
        }: {
          params?: PathParams<Path>;
          sport?: Sport;
        } = {}
      ) => generateSportPath(path, { params, sport: s }),
      // eslint-disable-next-line @typescript-eslint/no-shadow
      matchAnySportPath: (pattern: string, pathname: string, s?: Sport) =>
        matchAnySportPath(pattern, pathname, s || sport),
      generateSportContextPathPattern: (pattern: string) => {
        return `${SPORT_PATH_MAP[sport]}${pattern}`;
      },
    }),
    [sport]
  );

  return (
    <SportContext.Provider value={value}>{children}</SportContext.Provider>
  );
};
